import { ApiError } from '../errors';
import { carryCustomOptions, readCustomOptions } from './context';

/**
 * @typedef {import('openapi-fetch').Middleware} Middleware
 * @typedef {object} AuthConfig
 * @property {() => string | null | Promise<string | null>} getAccessToken
 * @property {() => Promise<string | null>} refreshAccessToken
 * @property {(endedBy?: ApiError) => void} onSessionEnded
 * @property {string} [refreshPath] default '/auth/refresh'
 */

/**
 * Bearer auth with single-flight refresh-on-401. Callers inject the token callbacks; nothing here
 * knows where tokens live. Skips the header when the per-call `noAuth` option is set.
 * @param {AuthConfig} config
 * @returns {Middleware}
 */
export function createAuthMiddleware(config) {
  const refreshPath = config.refreshPath ?? '/auth/refresh';
  let inFlightRefresh = null;
  /** An unread copy of each outgoing request with a body: fetch consumes the original's body. */
  const unsent = new WeakMap();

  function refreshOnce() {
    if (!inFlightRefresh) {
      inFlightRefresh = config.refreshAccessToken().finally(() => {
        inFlightRefresh = null;
      });
    }
    return inFlightRefresh;
  }

  return {
    async onRequest({ request }) {
      if (readCustomOptions(request).noAuth) {
        return undefined;
      }
      const token = await config.getAccessToken();
      let outgoing = request;
      if (token !== null) {
        const headers = new Headers(request.headers);
        headers.set('Authorization', `Bearer ${token}`);
        outgoing = carryCustomOptions(request, new Request(request, { headers }));
      }
      if (outgoing.method !== 'GET' && outgoing.method !== 'HEAD') {
        unsent.set(outgoing, outgoing.clone());
      }
      return outgoing;
    },

    async onResponse({ request, response, options }) {
      if (response.status !== 401 || readCustomOptions(request).noAuth) {
        return undefined;
      }

      if (new URL(request.url).pathname.endsWith(refreshPath)) {
        // The refresh endpoint's own 401. Only a SESSION_INVALID body ends the session; anything
        // else (another code, an unparseable body) keeps it. Never retried either way.
        const body = await response
          .clone()
          .json()
          .catch(() => undefined);
        if (body?.error?.code === 'SESSION_INVALID') {
          config.onSessionEnded();
        }
        return undefined;
      }

      // A deleted account: no refresh can revive it (refresh says the same), so end it here.
      const gate = await response
        .clone()
        .json()
        .catch(() => undefined);
      if (gate?.error?.code === 'SESSION_INVALID' && gate.error.reason === 'account_deleted') {
        config.onSessionEnded(ApiError.fromResponseBody(gate, 401));
        return undefined;
      }

      const token = await refreshOnce();
      if (token === null) {
        return undefined; // refresh failed: keep the original 401, don't loop
      }

      const headers = new Headers(request.headers);
      headers.set('Authorization', `Bearer ${token}`);
      const retry = carryCustomOptions(
        request,
        new Request(unsent.get(request) ?? request, { headers }),
      );
      // Called directly, outside the middleware chain: a 401 on the retry is returned as-is, so
      // there is no second refresh and no loop.
      return (options.fetch ?? fetch)(retry);
    },
  };
}
